import * as THREE from 'three/webgpu';

/**
 * 草图编辑视觉层级输入。
 */
export interface SketchEditingVisualLayeringInput {
    /** 当前正在编辑的草图根对象列表。 */
    currentSketchRoots?: readonly THREE.Object3D[];
    /** 其他草图根对象列表。 */
    otherSketchRoots?: readonly THREE.Object3D[];
    /** 预览根对象列表。 */
    previewRoots?: readonly THREE.Object3D[];
    /** 高亮的外部根对象列表。 */
    highlightedExternalRoots?: readonly THREE.Object3D[];
}

/**
 * 草图编辑视觉层级角色。
 */
type SketchEditingVisualLayeringRole = 'other' | 'current' | 'preview' | 'highlightedExternal';

/**
 * 对象渲染状态快照。
 */
interface ObjectRenderStateSnapshot {
    /** 对象本体。 */
    object: THREE.Object3D;
    /** 原始渲染层级。 */
    renderOrder: number;
}

/**
 * 材质深度状态快照。
 */
interface MaterialDepthStateSnapshot {
    /** 原始材质。 */
    material: DepthMutableMaterial;
    /** 原始深度测试状态。 */
    depthTest: boolean;
    /** 原始深度写入状态。 */
    depthWrite: boolean;
}

/**
 * 可修改深度状态的材质最小结构。
 * 说明：
 * 1. 只依赖 `uuid / depthTest / depthWrite / needsUpdate` 这组最小字段；
 * 2. 这样可兼容 `three` 与 `three/webgpu` 两种入口创建出来的材质实例；
 * 3. 视觉层级器不消费材质的其它能力，保持职责单一。
 */
interface DepthMutableMaterial {
    /** 材质唯一标识。 */
    uuid?: string;
    /** 深度测试开关。 */
    depthTest?: boolean;
    /** 深度写入开关。 */
    depthWrite?: boolean;
    /** 材质重编译标记。 */
    needsUpdate?: boolean;
}

/**
 * 可能携带关联点的对象最小结构。
 */
interface RelatedPointCarrier extends THREE.Object3D {
    /** 起点。 */
    startPoint?: THREE.Object3D;
    /** 终点。 */
    endPoint?: THREE.Object3D;
    /** 关联草图点快照。 */
    getRelatedSketchPointsSnapshot?: () => readonly THREE.Object3D[] | THREE.Object3D[];
}

/**
 * 草图编辑视觉层级统一入口。
 *
 * 职责：
 * 1. 统一维护当前编辑草图、其他草图、预览和外部高亮对象的视觉层级；
 * 2. 只处理 renderOrder、depthTest、depthWrite 这类纯视觉状态，不参与任何草图业务判断；
 * 3. 在清理时恢复对象与材质的原始状态，避免层级状态残留到后续交互。
 */
export class SketchEditingVisualLayering {
    /** 其他草图轮廓的渲染层级。 */
    public static readonly OTHER_CONTOUR_RENDER_ORDER = 100;

    /** 其他草图边对象的渲染层级。 */
    public static readonly OTHER_EDGE_RENDER_ORDER = 110;

    /** 其他草图点对象的渲染层级。 */
    public static readonly OTHER_POINT_RENDER_ORDER = 120;

    /** 当前草图轮廓的渲染层级。 */
    public static readonly CURRENT_CONTOUR_RENDER_ORDER = 300;

    /** 当前草图边对象的渲染层级。 */
    public static readonly CURRENT_EDGE_RENDER_ORDER = 310;

    /** 当前草图点对象的渲染层级。 */
    public static readonly CURRENT_POINT_RENDER_ORDER = 320;

    /** 外部高亮轮廓的渲染层级。 */
    public static readonly HIGHLIGHTED_EXTERNAL_CONTOUR_RENDER_ORDER = 500;

    /** 外部高亮边对象的渲染层级。 */
    public static readonly HIGHLIGHTED_EXTERNAL_EDGE_RENDER_ORDER = 510;

    /** 外部高亮点对象的渲染层级。 */
    public static readonly HIGHLIGHTED_EXTERNAL_POINT_RENDER_ORDER = 520;

    /** 预览轮廓的渲染层级。 */
    public static readonly PREVIEW_CONTOUR_RENDER_ORDER = 600;

    /** 预览边对象的渲染层级。 */
    public static readonly PREVIEW_EDGE_RENDER_ORDER = 610;

    /** 预览点对象的渲染层级。 */
    public static readonly PREVIEW_POINT_RENDER_ORDER = 620;

    /** 已修改对象的原始状态缓存。 */
    private readonly objectStateMap: Map<string, ObjectRenderStateSnapshot> = new Map();

    /** 已修改材质的原始状态缓存。 */
    private readonly materialStateMap: Map<string, MaterialDepthStateSnapshot> = new Map();

    /**
     * 同步草图编辑视觉层级。
     *
     * 说明：
     * 1. 每次同步前先清理旧状态，避免历史层级影响后续编辑态；
     * 2. 按 `other -> current -> highlightedExternal -> preview` 顺序写入，保证 preview 始终位于最上层；
     * 3. 所有角色都统一补遍历关联点，避免 detached 草图点逃逸出层级控制；
     * 4. 预览与外部高亮暂由可选参数保留，不在 Viewer 中硬编码具体业务来源。
     *
     * @param input 草图编辑视觉层级输入。
     */
    public apply(input: SketchEditingVisualLayeringInput): void {
        this.clear();
        this.applyRoots(input.otherSketchRoots ?? [], 'other', true);
        this.applyRoots(input.currentSketchRoots ?? [], 'current', true);
        this.applyRoots(input.highlightedExternalRoots ?? [], 'highlightedExternal', true);
        this.applyRoots(input.previewRoots ?? [], 'preview', true);
    }

    /**
     * 同步草图编辑视觉层级。
     *
     * 说明：
     * 1. 保留 `sync()` 作为 Viewer 侧更贴近宿主语义的入口；
     * 2. 内部直接复用 `apply()`，避免两套实现出现偏差；
     * 3. 这样既兼容当前 core 入口命名，也与参考分支的 `apply()` 语义保持一致。
     *
     * @param input 草图编辑视觉层级输入。
     */
    public sync(input: SketchEditingVisualLayeringInput): void {
        this.apply(input);
    }

    /**
     * 清理当前应用的视觉层级并恢复原始状态。
     *
     * 说明：
     * 1. 仅恢复本类曾经改写过的对象与材质；
     * 2. 清理后缓存立即释放，避免下一次同步误用旧快照；
     * 3. 该方法可重复调用，保持幂等。
     */
    public clear(): void {
        this.objectStateMap.forEach((snapshot) => {
            snapshot.object.renderOrder = snapshot.renderOrder;
        });

        this.materialStateMap.forEach((snapshot) => {
            snapshot.material.depthTest = snapshot.depthTest;
            snapshot.material.depthWrite = snapshot.depthWrite;
            snapshot.material.needsUpdate = true;
        });

        this.objectStateMap.clear();
        this.materialStateMap.clear();
    }

    /**
     * 按角色应用一组根对象。
     *
     * @param roots 根对象列表。
     * @param role 视觉层级角色。
     * @param includeRelatedPoints
     */
    private applyRoots(
        roots: readonly THREE.Object3D[],
        role: SketchEditingVisualLayeringRole,
        includeRelatedPoints: boolean = false,
    ): void {
        roots.forEach((root) => {
            this.walkLayeringObjects(root, includeRelatedPoints, (object) => {
                this.applyToObject(object, role);
            });
        });
    }

    /**
     * 对单个对象应用目标视觉层级。
     *
     * @param object 当前遍历到的对象。
     * @param role 视觉层级角色。
     */
    private applyToObject(object: THREE.Object3D, role: SketchEditingVisualLayeringRole): void {
        const renderOrder = this.resolveRenderOrder(object, role);
        if (renderOrder === null) {
            return;
        }

        this.captureObjectState(object);
        object.renderOrder = renderOrder;
        this.applyMaterialDepthState(object);
    }

    /**
     * 解析对象在指定角色下应使用的渲染层级。
     *
     * @param object 当前对象。
     * @param role 视觉层级角色。
     * @returns 目标渲染层级；若对象不属于受控草图对象则返回 null。
     */
    private resolveRenderOrder(
        object: THREE.Object3D,
        role: SketchEditingVisualLayeringRole,
    ): number | null {
        if (this.isPointLikeObject(object)) {
            return this.resolvePointRenderOrder(role);
        }

        if (this.isLineLikeObject(object)) {
            return this.resolveEdgeRenderOrder(role);
        }

        if (this.isContourLikeObject(object)) {
            return this.resolveContourRenderOrder(role);
        }

        return null;
    }

    /**
     * 解析轮廓对象的目标渲染层级。
     *
     * @param role 视觉层级角色。
     * @returns 轮廓对象渲染层级。
     */
    private resolveContourRenderOrder(role: SketchEditingVisualLayeringRole): number {
        switch (role) {
            case 'other':
                return SketchEditingVisualLayering.OTHER_CONTOUR_RENDER_ORDER;
            case 'current':
                return SketchEditingVisualLayering.CURRENT_CONTOUR_RENDER_ORDER;
            case 'preview':
                return SketchEditingVisualLayering.PREVIEW_CONTOUR_RENDER_ORDER;
            case 'highlightedExternal':
                return SketchEditingVisualLayering.HIGHLIGHTED_EXTERNAL_CONTOUR_RENDER_ORDER;
            default:
                throw new Error('未知草图编辑视觉层级角色。');
        }
    }

    /**
     * 解析边对象的目标渲染层级。
     *
     * @param role 视觉层级角色。
     * @returns 边对象渲染层级。
     */
    private resolveEdgeRenderOrder(role: SketchEditingVisualLayeringRole): number {
        switch (role) {
            case 'other':
                return SketchEditingVisualLayering.OTHER_EDGE_RENDER_ORDER;
            case 'current':
                return SketchEditingVisualLayering.CURRENT_EDGE_RENDER_ORDER;
            case 'preview':
                return SketchEditingVisualLayering.PREVIEW_EDGE_RENDER_ORDER;
            case 'highlightedExternal':
                return SketchEditingVisualLayering.HIGHLIGHTED_EXTERNAL_EDGE_RENDER_ORDER;
            default:
                throw new Error('未知草图编辑视觉层级角色。');
        }
    }

    /**
     * 解析点对象的目标渲染层级。
     *
     * @param role 视觉层级角色。
     * @returns 点对象渲染层级。
     */
    private resolvePointRenderOrder(role: SketchEditingVisualLayeringRole): number {
        switch (role) {
            case 'other':
                return SketchEditingVisualLayering.OTHER_POINT_RENDER_ORDER;
            case 'current':
                return SketchEditingVisualLayering.CURRENT_POINT_RENDER_ORDER;
            case 'preview':
                return SketchEditingVisualLayering.PREVIEW_POINT_RENDER_ORDER;
            case 'highlightedExternal':
                return SketchEditingVisualLayering.HIGHLIGHTED_EXTERNAL_POINT_RENDER_ORDER;
            default:
                throw new Error('未知草图编辑视觉层级角色。');
        }
    }

    /**
     * 记录对象的原始渲染层级。
     *
     * @param object 当前对象。
     */
    private captureObjectState(object: THREE.Object3D): void {
        if (this.objectStateMap.has(object.uuid)) {
            return;
        }

        this.objectStateMap.set(object.uuid, {
            object,
            renderOrder: object.renderOrder,
        });
    }

    /**
     * 对对象材质应用统一的深度状态。
     *
     * 说明：
     * 1. 草图编辑视觉层级只关心遮挡关系，不改动材质颜色和透明度；
     * 2. 深度测试与深度写入同时关闭，保证共面草图在宿主面前稳定显示；
     * 3. 多材质对象逐个处理，避免局部材质漏改。
     *
     * @param object 当前对象。
     */
    private applyMaterialDepthState(object: THREE.Object3D): void {
        this.collectDepthMutableMaterials(object).forEach((material) => {
            this.captureMaterialState(material);
            material.depthTest = false;
            material.depthWrite = false;
            material.needsUpdate = true;
        });
    }

    /**
     * 记录材质的原始深度状态。
     *
     * @param material 当前材质。
     */
    private captureMaterialState(material: DepthMutableMaterial): void {
        const materialId = typeof material.uuid === 'string' ? material.uuid : null;
        if (!materialId || this.materialStateMap.has(materialId)) {
            return;
        }

        this.materialStateMap.set(materialId, {
            material,
            depthTest: material.depthTest ?? true,
            depthWrite: material.depthWrite ?? true,
        });
    }

    /**
     * 遍历视觉层级对象。
     * 说明：
     * 1. 草图点可能被重挂到根对象子树外，不能假设只靠 `children` 就能完整覆盖；
     * 2. 因此需要按需补遍历 `startPoint / endPoint / getRelatedSketchPointsSnapshot()`；
     * 3. 使用 visited 去重，避免一个点对象被多次覆盖 renderOrder。
     *
     * @param root 遍历起点。
     * @param includeRelatedPoints 是否补遍历关联点。
     * @param visitor 访问回调。
     */
    private walkLayeringObjects(
        root: THREE.Object3D,
        includeRelatedPoints: boolean,
        visitor: (object: THREE.Object3D) => void,
    ): void {
        const visited = new Set<THREE.Object3D>();

        const visit = (object: THREE.Object3D): void => {
            if (visited.has(object)) {
                return;
            }

            visited.add(object);
            visitor(object);
            object.children.forEach((child) => {
                visit(child);
            });
            if (!includeRelatedPoints) {
                return;
            }
            this.getRelatedPoints(object).forEach((pointObject) => {
                visit(pointObject);
            });
        };

        visit(root);
    }

    /**
     * 提取对象关联的点对象。
     *
     * @param object 当前对象。
     * @returns 关联点对象集合。
     */
    private getRelatedPoints(object: THREE.Object3D): THREE.Object3D[] {
        const targetObject = object as RelatedPointCarrier;
        const relatedPoints = new Set<THREE.Object3D>();

        if (this.isObject3DLike(targetObject.startPoint)) {
            relatedPoints.add(targetObject.startPoint);
        }
        if (this.isObject3DLike(targetObject.endPoint)) {
            relatedPoints.add(targetObject.endPoint);
        }
        if (typeof targetObject.getRelatedSketchPointsSnapshot !== 'function') {
            return [...relatedPoints];
        }

        targetObject.getRelatedSketchPointsSnapshot().forEach((pointObject) => {
            if (this.isObject3DLike(pointObject)) {
                relatedPoints.add(pointObject);
            }
        });
        return [...relatedPoints];
    }

    /**
     * 收集对象上可修改深度状态的材质集合。
     *
     * @param object 当前对象。
     * @returns 材质集合。
     */
    private collectDepthMutableMaterials(object: THREE.Object3D): DepthMutableMaterial[] {
        const rawMaterial = Reflect.get(object, 'material');
        if (Array.isArray(rawMaterial)) {
            return rawMaterial.filter((material) => this.isDepthMutableMaterial(material));
        }

        return this.isDepthMutableMaterial(rawMaterial) ? [rawMaterial] : [];
    }

    /**
     * 判断当前对象是否为点状对象。
     *
     * @param object 当前对象。
     * @returns 若对象带有点状运行时标记则返回 true。
     */
    private isPointLikeObject(object: THREE.Object3D): boolean {
        return Reflect.get(object, 'isSketchPointObject') === true
            || Reflect.get(object, 'isPoints') === true
            || Reflect.get(object, 'isSprite') === true;
    }

    /**
     * 判断当前对象是否为线状对象。
     *
     * @param object 当前对象。
     * @returns 若对象带有线状运行时标记则返回 true。
     */
    private isLineLikeObject(object: THREE.Object3D): boolean {
        return Reflect.get(object, 'isAbstractSketchEdgeObject') === true
            || Reflect.get(object, 'isLine') === true;
    }

    /**
     * 判断当前对象是否为轮廓对象。
     *
     * @param object 当前对象。
     * @returns 若对象带有轮廓运行时标记则返回 true。
     */
    private isContourLikeObject(object: THREE.Object3D): boolean {
        return Reflect.get(object, 'isSketchContour') === true
            || Reflect.get(object, 'isMesh') === true;
    }

    /**
     * 判断材质是否支持深度状态修改。
     *
     * @param material 当前材质。
     * @returns 支持深度状态开关时返回 true。
     */
    private isDepthMutableMaterial(material: unknown): material is DepthMutableMaterial {
        return Boolean(material) && typeof material === 'object';
    }

    /**
     * 判断值是否满足 Object3D 最小结构。
     *
     * @param value 待判断值。
     * @returns 满足最小结构时返回 true。
     */
    private isObject3DLike(value: unknown): value is THREE.Object3D {
        return Boolean(value)
            && typeof value === 'object'
            && typeof Reflect.get(value, 'uuid') === 'string'
            && Array.isArray(Reflect.get(value, 'children'));
    }
}
